/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type IntlService from 'embercom/services/intl';
import type CustomEmailAddress from 'embercom/models/custom-email-address';
import type DkimSettings from 'embercom/models/dkim-settings';
import type DetectIntercomradesDomainService from 'embercom/services/detect-intercomrades-domain-service';
import { get } from 'embercom/lib/ajax';
import { canUseFinEmailSenderSettings } from 'embercom/lib/fin-email-sender-settings';
import type { ChannelType } from 'embercom/lib/workflows/fin-workflow-preview';
import { objectTypes, states } from 'embercom/models/data/matching-system/matching-constants';
import { FIN_SETUP_BEHAVIOR_ENDPOINT } from 'embercom/components/operator/fin/setup/settings';
import { EntityType } from 'embercom/models/data/entity-types';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type KnowledgeHubService from './knowledge-hub-service';
import { dropTask } from 'ember-concurrency-decorators';
import { didCancel } from 'ember-concurrency';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

type ValidationResult =
  | 'no_custom_sender'
  | 'default_reply_invalid'
  | 'invalid_fin_email_address'
  | 'no_fin_email_address';
type ValidationResponse = { isValid: boolean; type?: ValidationResult; message?: string };

interface FinStats {
  finUsableArticlesCount: number;
  finUsableExternalContentCount: number;
  finUsableSnippetsCount: number;
  finUsableActionsCount: number;
  finUsableFilesCount: number;
  liveFinProfilesCount: number;
  workflowsCount: WorkflowStats;
}

interface WorkflowStats {
  liveFinChatWorkflowsCount: number;
  liveFinEmailWorkflowsCount: number;
  liveFinPhoneWorkflowsCount: number;
  liveFinWorkflowsCount: number;
  hasFinChatWorkflows: boolean;
  hasFinEmailWorkflows: boolean;
  hasFinPhoneWorkflows: boolean;
}

interface SetupRuleset {
  emailSetupRuleset: $TSFixMe;
  finSetupRuleset: $TSFixMe;
  phoneSetupRuleset: $TSFixMe;
}

export default class AiAgentSetupService extends Service {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare detectIntercomradesDomainService: DetectIntercomradesDomainService;
  @service declare intl: IntlService;
  @service declare contentImportService: $TSFixMe;
  @service declare outboundHomeService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;

  @tracked finStats: FinStats;
  @tracked setupRuleset: SetupRuleset;
  @tracked liveResolutionBotBehaviors: $TSFixMe;
  @tracked customAnswerCounts: {
    all: number;
    finUsable: number;
  };
  @tracked totalLiveWorkflowsCount = 0;
  @tracked totalPausedWorkflowsCount = 0;
  @tracked totalPausedFinWorkflowsCount = 0;
  @tracked workflowsCountLoaded = false;
  @tracked setupRulesetsLoaded = false;
  // This channel type should be set at the route level and can be used in components instead of prop drilling channel type everywhere from the route template
  @tracked channelType: ChannelType = 'chat';
  setupRulesetsLoadedWithForceCreate = false; // Track if we've loaded with force_create: true
  defaultReplyInvalidMessage: string;
  invalidFinEmailAddressMessage: string;
  isLoading = false;
  lastFetchTime = 0;
  fetchInterval = 10000; // 10 seconds
  loadFinPromise: Promise<any> | null = null;

  constructor() {
    super(...arguments);
    this.defaultReplyInvalidMessage = this.intl.t(
      'operator.workflows.visual-builder.validations.fin.email.default-reply-invalid',
    );
    this.invalidFinEmailAddressMessage = this.intl.t(
      'operator.workflows.visual-builder.validations.fin.email.invalid-fin-email-address',
    );
    this.finStats = {
      finUsableArticlesCount: 0,
      finUsableExternalContentCount: 0,
      finUsableSnippetsCount: 0,
      finUsableActionsCount: 0,
      finUsableFilesCount: 0,
      liveFinProfilesCount: 0,
      workflowsCount: {
        liveFinChatWorkflowsCount: 0,
        liveFinEmailWorkflowsCount: 0,
        liveFinPhoneWorkflowsCount: 0,
        liveFinWorkflowsCount: 0,
        hasFinChatWorkflows: false,
        hasFinEmailWorkflows: false,
        hasFinPhoneWorkflows: false,
      },
    };
    this.setupRuleset = {
      emailSetupRuleset: null,
      finSetupRuleset: null,
      phoneSetupRuleset: null,
    };
    this.customAnswerCounts = {
      all: 0,
      finUsable: 0,
    };
  }

  get app() {
    return this.appService.app;
  }

  get validateEmailSetup(): ValidationResponse {
    if (this.detectIntercomradesDomainService.isIntercomradesDomain) {
      return { isValid: true };
    }

    // Emails are sent through the third party service in Fin Standalone apps.
    // E.g. via Zendesk of Salesforce
    if (this.app.canUseStandalone) {
      return { isValid: true };
    }

    if (canUseFinEmailSenderSettings(this.app) && this.app.hasFinDefaultEmailAddressId) {
      return this.validateFinEmailAddress;
      // Keeping it here, we will need it once fin email address requirements
      // are fully enforced
      // {
      //   isValid: false,
      //   type: 'no_fin_email_address',
      //   message: this.intl.t(
      //     'operator.workflows.visual-builder.validations.fin.email.no-fin-email-address',
      //   ),
      // };
    }

    if (!this.app.use_admin_app_email) {
      return {
        isValid: false,
        type: 'no_custom_sender',
        message: this.intl.t(
          'operator.workflows.visual-builder.validations.fin.email.no-custom-sender',
        ),
      };
    }

    if (!this.app.hasAdminReplyDefaultAddress) {
      return { isValid: false, type: 'no_custom_sender', message: this.defaultReplyInvalidMessage };
    }

    if (!this.validateEmailDefaultAddress.isValid) {
      return this.validateEmailDefaultAddress;
    }

    if (!this.validateDkimSettings.isValid) {
      return this.validateDkimSettings;
    }

    return { isValid: true };
  }

  get defaultReplyAddress() {
    return this.app.customEmailAddresses.find(
      (item: CustomEmailAddress) => item.id === this.app.admin_reply_default_address_id,
    );
  }

  get finReplyAddress() {
    return this.app.customEmailAddresses.find((item: CustomEmailAddress) => {
      return (
        String(item.senderEmailAddressSettingsId) === String(this.app.fin_default_email_address_id)
      );
    });
  }

  get validateFinEmailAddress(): ValidationResponse {
    if (this.app.customEmailAddresses.isPending) {
      return { isValid: true };
    }

    // TODO: enforce forwarding enabled once fin email address is fully enforced
    // add this: !this.finReplyAddress?.forwardingEnabled
    if (!this.finReplyAddress || !this.finReplyAddress?.verified) {
      return {
        isValid: false,
        type: 'invalid_fin_email_address',
        message: this.invalidFinEmailAddressMessage,
      };
    }

    if (!this.validateFinDkimSettings.isValid) {
      return this.validateFinDkimSettings;
    }

    return { isValid: true };
  }

  get validateFinDkimSettings(): ValidationResponse {
    // If Promise is pending, we are still fetching the dkim_settings
    if (this.finReplyAddress && !this.app.dkim_settings.isPending) {
      let dkimSettings = this.app.dkim_settings.find((setting: DkimSettings) =>
        this.finReplyAddress?.email.toLowerCase().endsWith(`@${setting.domain}`),
      );
      if (!dkimSettings || !dkimSettings.validRecordExists || !dkimSettings.validDmarcExists) {
        return {
          isValid: false,
          type: 'invalid_fin_email_address',
          message: this.invalidFinEmailAddressMessage,
        };
      }
    }
    return { isValid: true };
  }

  get validateEmailDefaultAddress(): ValidationResponse {
    // If Promise is pending, we are still fetching the customEmailAddresses
    if (!this.app.customEmailAddresses.isPending) {
      if (!this.defaultReplyAddress || !this.defaultReplyAddress?.verified) {
        return {
          isValid: false,
          type: 'default_reply_invalid',
          message: this.defaultReplyInvalidMessage,
        };
      }
    }
    return { isValid: true };
  }

  get validateDkimSettings(): ValidationResponse {
    // If Promise is pending, we are still fetching the dkim_settings
    if (this.defaultReplyAddress && !this.app.dkim_settings.isPending) {
      let dkimSettings = this.app.dkim_settings.find((setting: DkimSettings) =>
        this.defaultReplyAddress?.email.toLowerCase().endsWith(`@${setting.domain}`),
      );
      if (!dkimSettings || !dkimSettings.validRecordExists || !dkimSettings.validDmarcExists) {
        return {
          isValid: false,
          type: 'default_reply_invalid',
          message: this.defaultReplyInvalidMessage,
        };
      }
    }
    return { isValid: true };
  }

  async load(forceCreate = false) {
    // if there are a lot of workflows, it takes a while to load the data
    // so we don't want to call the api too often in a short period of time
    let now = Date.now();

    // Allow bypassing the time-based cache if we're requesting force_create: true
    // but haven't loaded with it yet (to ensure deploy pages can create the behaviors)
    let shouldBypassCache = forceCreate && !this.setupRulesetsLoadedWithForceCreate;

    if (!shouldBypassCache && (this.isLoading || now - this.lastFetchTime < this.fetchInterval)) {
      return this.loadFinPromise;
    }

    this.lastFetchTime = now;
    this.isLoading = true;
    let workflowsCountPromise = this.loadFinWorkflowsCount();
    let setupRulesetPromise = this.loadSetupRulesets(forceCreate);
    let liveResolutionBotBehaviorsPromise = this.loadLiveResolutionBotBehaviors();
    let customAnswerCountsPromise = this.loadCustomAnswerCounts();
    let knowledgeUsageSummaryPromise = this.loadKnowledgeUsageSummary();

    this.loadFinPromise = Promise.all([
      workflowsCountPromise,
      setupRulesetPromise,
      liveResolutionBotBehaviorsPromise,
      customAnswerCountsPromise,
      knowledgeUsageSummaryPromise,
    ]).finally(() => {
      this.isLoading = false;
    });
    return this.loadFinPromise;
  }

  async loadCustomAnswerCounts() {
    let response = await get('/ember/custom_answers/counts', { app_id: this.appService.app.id });

    this.customAnswerCounts = {
      all: response.all || 0,
      finUsable: response.live || 0,
    };
  }

  async loadKnowledgeUsageSummary() {
    let knowledgeUsageSummary = await this.knowledgeHubService.fetchKnowledgeUsageSummary();

    let finStats = structuredClone(this.finStats);
    finStats.finUsableArticlesCount = knowledgeUsageSummary[EntityType.ArticleContent]?.agent || 0;
    finStats.finUsableExternalContentCount =
      knowledgeUsageSummary[EntityType.ExternalContent]?.agent || 0;
    finStats.finUsableSnippetsCount = knowledgeUsageSummary[EntityType.ContentSnippet]?.agent || 0;
    finStats.finUsableActionsCount =
      knowledgeUsageSummary[EntityType.WorkflowConnectorAction]?.agent || 0;
    finStats.finUsableFilesCount = knowledgeUsageSummary[EntityType.FileSourceContent]?.agent || 0;
    this.finStats = finStats;
  }

  get allContentCount() {
    return (
      this.finStats.finUsableArticlesCount +
      this.finStats.finUsableExternalContentCount +
      this.finStats.finUsableSnippetsCount +
      this.finStats.finUsableFilesCount
    );
  }

  async loadFinWorkflowsCount() {
    if (this.workflowsCountLoaded) {
      return;
    }

    let response: {
      live_chat_count: number;
      live_email_count: number;
      live_phone_call_count: number;
      has_chat_workflows: boolean;
      has_email_workflows: boolean;
      has_phone_workflows: boolean;
    } = await get('/ember/operator_workflows/get_live_fin_workflows_count', {
      app_id: this.app.id,
    });

    let finStats = structuredClone(this.finStats);
    finStats.workflowsCount.liveFinChatWorkflowsCount = response.live_chat_count ?? 0;
    finStats.workflowsCount.liveFinEmailWorkflowsCount = response.live_email_count ?? 0;
    finStats.workflowsCount.liveFinPhoneWorkflowsCount = response.live_phone_call_count ?? 0;
    finStats.workflowsCount.liveFinWorkflowsCount =
      (response.live_chat_count ?? 0) +
      (response.live_email_count ?? 0) +
      (response.live_phone_call_count ?? 0);
    finStats.workflowsCount.hasFinChatWorkflows = response.has_chat_workflows;
    finStats.workflowsCount.hasFinEmailWorkflows = response.has_email_workflows;
    finStats.workflowsCount.hasFinPhoneWorkflows = response.has_phone_workflows;
    this.finStats = finStats;
    this.workflowsCountLoaded = true;
  }

  async loadPausedWorkflowsCount() {
    let pausedWorkflowsSearch = await this.outboundHomeService.contentSearch({
      object_types: [
        objectTypes.customBot,
        objectTypes.inboundCustomBot,
        objectTypes.buttonCustomBot,
        objectTypes.triggerableCustomBot,
      ],
      states: [states.paused],
      app_id: this.app.id,
      per_page: 1, // Only need count, not the actual data
    });

    this.totalPausedWorkflowsCount = pausedWorkflowsSearch.totalCount ?? 0;
  }

  async loadPausedFinWorkflowsCount() {
    let pausedFinWorkflowsSearch = await this.outboundHomeService.contentSearch({
      object_types: [
        objectTypes.customBot,
        objectTypes.inboundCustomBot,
        objectTypes.buttonCustomBot,
        objectTypes.triggerableCustomBot,
      ],
      states: [states.paused],
      app_id: this.app.id,
      additionalSearchableData: {
        has_fin: true,
      },
      per_page: 1, // Only need count, not the actual data
    });

    this.totalPausedFinWorkflowsCount = pausedFinWorkflowsSearch.totalCount ?? 0;
  }

  async reloadFinWorkflowsCount() {
    this.workflowsCountLoaded = false;
    await this.loadFinWorkflowsCount();
  }

  async loadTotalWorkflowsCount() {
    let liveWorkflowsSearch = await this.outboundHomeService.contentSearch({
      object_types: [
        objectTypes.customBot,
        objectTypes.inboundCustomBot,
        objectTypes.buttonCustomBot,
        objectTypes.triggerableCustomBot,
      ],
      states: [states.live],
      app_id: this.app.id,
      per_page: 1, // Only need count, not the actual data
    });

    this.totalLiveWorkflowsCount = liveWorkflowsSearch.totalCount ?? 0;
  }

  async reloadWorkflowsCounts() {
    this.workflowsCountLoaded = false;
    await Promise.all([
      this.loadFinWorkflowsCount(),
      this.loadTotalWorkflowsCount(),
      this.loadPausedWorkflowsCount(),
      this.loadPausedFinWorkflowsCount(),
    ]);
  }

  async loadSetupRulesets(forceCreate = false) {
    taskFor(this.loadSetupRulesetsTask).perform(forceCreate);

    try {
      if (taskFor(this.loadSetupRulesetsTask).isRunning) {
        return taskFor(this.loadSetupRulesetsTask).last;
      }
    } catch (error) {
      // Ember concurrency throws an error when a task is cancelled because we're converting it to a Promise.
      // We want this API to return a Promise, so it's perfectly fine to ignore that error.
      // For more information: https://ember-concurrency.com/docs/task-cancelation-help
      if (!didCancel(error)) {
        throw error;
      }
    }
  }

  @dropTask
  *loadSetupRulesetsTask(forceCreate: boolean): TaskGenerator<void> {
    // If we're requesting force_create: true but haven't loaded with it yet, we need to refetch
    // If we've already loaded with force_create: true, we don't need to load again
    if (forceCreate && this.setupRulesetsLoadedWithForceCreate) {
      return;
    }

    // For non-force requests, if we've loaded either way, we're good
    if (!forceCreate && this.setupRulesetsLoaded) {
      return;
    }

    yield Promise.all([
      this.loadSetupRuleset('email', forceCreate),
      this.loadSetupRuleset('chat', forceCreate),
      this.loadSetupRuleset('phone', forceCreate),
    ]);

    this.setupRulesetsLoaded = true;
    if (forceCreate) {
      this.setupRulesetsLoadedWithForceCreate = true;
    }
  }

  async loadSetupRuleset(setupType: ChannelType, forceCreate = false) {
    let existingRuleset;

    switch (setupType) {
      case 'email':
        existingRuleset = this.setupRuleset.emailSetupRuleset;
        break;
      case 'phone':
        existingRuleset = this.setupRuleset.phoneSetupRuleset;
        break;
      case 'chat':
        existingRuleset = this.setupRuleset.finSetupRuleset;
        break;
    }

    let ruleset;

    // If we have an existing ruleset (not null), reload it to get fresh data
    // Note: existingRuleset could be null if we previously determined it doesn't exist
    if (existingRuleset !== null && existingRuleset !== undefined) {
      await existingRuleset.reload();
      ruleset = existingRuleset;
    } else {
      // We don't have a ruleset, either because:
      // 1. We've never fetched before
      // 2. We previously fetched with force_create: false and it didn't exist (existingRuleset === null)
      // In both cases, fetch it (the force_create parameter will determine if it gets created)
      let response = await this.fetchSetupRuleset(setupType, forceCreate);
      // When force_create is false and the profile doesn't exist, the backend returns 204 No Content
      // In this case, response will be null/undefined and we set ruleset to null
      if (response) {
        this.store.pushPayload({ 'matching-system/ruleset': response });
        ruleset = this.store.peekRecord('matching-system/ruleset', response.id);
      } else {
        ruleset = null;
      }
    }

    switch (setupType) {
      case 'email':
        this.setupRuleset = { ...this.setupRuleset, emailSetupRuleset: ruleset };
        break;
      case 'phone':
        this.setupRuleset = { ...this.setupRuleset, phoneSetupRuleset: ruleset };
        break;
      case 'chat':
        this.setupRuleset = { ...this.setupRuleset, finSetupRuleset: ruleset };
        break;
    }
  }

  async fetchSetupRuleset(setupType: ChannelType, forceCreate = false) {
    let endpoint = FIN_SETUP_BEHAVIOR_ENDPOINT;
    return get(endpoint, {
      app_id: this.appService.app.id,
      setup_type: setupType,
      force_create: forceCreate,
    });
  }

  async loadLiveResolutionBotBehaviors() {
    this.liveResolutionBotBehaviors = await this.outboundHomeService.contentSearch({
      object_types: [objectTypes.resolutionBotBehavior],
      states: [states.live],
      app_id: this.appService.app.id,
    });

    let liveFinProfilesCount = 0;

    this.liveResolutionBotBehaviors.contentWrappers?.forEach((contentWrapper: ContentWrapper) => {
      let content = contentWrapper.contents.firstObject;
      if (content?.contentData?.use_ai_answers || content?.contentData?.use_custom_answers) {
        liveFinProfilesCount += 1;
      }
    });

    let finStats = structuredClone(this.finStats);
    finStats.liveFinProfilesCount = liveFinProfilesCount;
    this.finStats = finStats;
  }

  get isFinLive() {
    return (
      this.isFinSetupEmailRulesetLive ||
      this.isFinSetupChatRulesetLive ||
      this.isFinSetupPhoneRulesetLive ||
      this.finStats.workflowsCount.liveFinChatWorkflowsCount > 0
    );
  }

  get totalFinLiveChatCount(): number {
    return (
      this.finStats.workflowsCount.liveFinChatWorkflowsCount + this.finStats.liveFinProfilesCount
    );
  }

  get totalContentCount(): number {
    return (
      (this.knowledgeHubService.usageSummary?.[EntityType.ArticleContent]?.agent || 0) +
      (this.knowledgeHubService.usageSummary?.[EntityType.ContentSnippet]?.agent || 0) +
      (this.knowledgeHubService.usageSummary?.[EntityType.Answer]?.agent || 0) +
      (this.knowledgeHubService.usageSummary?.[EntityType.FileSourceContent]?.agent || 0) +
      (this.knowledgeHubService.usageSummary?.[EntityType.ExternalContent]?.agent || 0)
    );
  }

  get hasContentReadyForFin(): boolean {
    return (
      !!this.knowledgeHubService.usageSummary &&
      (this.knowledgeHubService.usageSummary[EntityType.ArticleContent]?.agent > 0 ||
        this.knowledgeHubService.usageSummary[EntityType.Answer]?.agent > 0 ||
        this.knowledgeHubService.usageSummary[EntityType.ExternalContent]?.agent > 0 ||
        this.knowledgeHubService.usageSummary[EntityType.ContentSnippet]?.agent > 0 ||
        this.knowledgeHubService.usageSummary[EntityType.WorkflowConnectorAction]?.agent > 0 ||
        this.knowledgeHubService.usageSummary[EntityType.FileSourceContent]?.agent > 0)
    );
  }

  get isFinSetupChatRulesetLive(): boolean {
    return this.setupRuleset.finSetupRuleset?.get('state') === states.live;
  }

  get isFinSetupEmailRulesetLive(): boolean {
    return this.setupRuleset.emailSetupRuleset?.get('state') === states.live;
  }

  get isFinSetupPhoneRulesetLive(): boolean {
    return this.setupRuleset.phoneSetupRuleset?.get('state') === states.live;
  }

  get finSetupRulesetStateIsLive(): boolean {
    return (
      this.isFinSetupChatRulesetLive ||
      this.isFinSetupEmailRulesetLive ||
      this.isFinSetupPhoneRulesetLive
    );
  }

  isFinLiveInChannelOutsideSetup(channelType: ChannelType, includeProfiles = true): boolean {
    switch (channelType) {
      case 'email':
        return this.finStats.workflowsCount.liveFinEmailWorkflowsCount > 0;
      case 'phone':
        return this.finStats.workflowsCount.liveFinPhoneWorkflowsCount > 0;
      case 'chat':
        return includeProfiles
          ? this.totalFinLiveChatCount > 0
          : this.finStats.workflowsCount.liveFinChatWorkflowsCount > 0;
    }
  }

  isFinLiveIncludingChannelsOutsideSetup(): boolean {
    return (
      this.isFinLive ||
      this.isFinLiveInChannelOutsideSetup('email') ||
      this.isFinLiveInChannelOutsideSetup('phone') ||
      this.isFinLiveInChannelOutsideSetup('chat')
    );
  }

  get isFinChatLive(): boolean {
    return this.isFinSetupChatRulesetLive || this.isFinLiveInChannelOutsideSetup('chat');
  }

  get isFinEmailLive(): boolean {
    return this.isFinSetupEmailRulesetLive || this.isFinLiveInChannelOutsideSetup('email');
  }

  get isFinPhoneLive(): boolean {
    return this.isFinSetupPhoneRulesetLive || this.isFinLiveInChannelOutsideSetup('phone');
  }

  get isAnyFinChannelLive(): boolean {
    return this.isFinChatLive || this.isFinEmailLive || this.isFinPhoneLive;
  }

  get isFinLiveInWorkflowsForCurrentChannel() {
    return this.isFinLiveInChannelOutsideSetup(this.channelType, false);
  }

  get shouldShowOnlyOneDeployMethod() {
    /*
      Update (Oct 2025):
      We want to show only one of either simple deploy or workflows going forward.
      Right now, customers can potentially have both enabled at the same time.
      The flag fin-first-only-show-one-fin-deploy-method is used to rollout this change to new customers first,
      before making the necessary billing changes and rolling out to everyone.
      The conditions are:
      1. The app must be opted in the flag,
      2. If the app has access to workflows, it must not have simple deploy live - otherwise we'd be hiding a live feature.
    */

    let isWorkflowCustomer = this.appService.app.canUseFeature('workflows_core_billing_feature');

    let workflowCustomerHasLiveSimpleDeployForChat =
      isWorkflowCustomer && this.channelType === 'chat' && this.isFinSetupChatRulesetLive;

    let workflowCustomerHasLiveSimpleDeployForEmail =
      isWorkflowCustomer && this.channelType === 'email' && this.isFinSetupEmailRulesetLive;

    return (
      this.appService.app.canUseFeature('fin-first-only-show-one-fin-deploy-method') &&
      !workflowCustomerHasLiveSimpleDeployForChat &&
      !workflowCustomerHasLiveSimpleDeployForEmail
    );
  }

  get shouldShowPreview() {
    if (this.channelType === 'phone') {
      return false;
    }

    if (
      this.shouldShowOnlyOneDeployMethod &&
      this.appService.app.canUseFeature('workflows_core_billing_feature') &&
      this.channelType === 'chat'
    ) {
      return false;
    }

    return true;
  }
}
